import Immutable from 'seamless-immutable';

import {
  PAGE_TYPE_CHANGE,
  RECEIVE_SEARCH_RESPONSE,
  REQUEST_SEARCH,
  RESET_FACET_GROUP,
  TOGGLE_FACET_CHOSEN,
  TOGGLE_FACETABLE_VIEW,
  TOGGLE_FACETS,
  TOGGLE_SELECTED_FACET,
  TOGGLE_SELECTED_FACET_GROUP
} from 'constants/reduxActions';
import {
  BEST_FOR_YOU_FACETFIELD,
  SINGLE_SELECT_FILTERS
} from 'constants/appConstants';

import { FILTERS_RE } from 'common/regex';
import marketplace from 'cfg/marketplace.json';

const { search: { onlyOneGroupOpen, topBrands, sizeFacetFields, suppressFiltersLinkHrefs } } = marketplace;

const initialState = {
  toDisplay: [],
  chosenFacetGroup: null,
  requestedUrl: null,
  mobileFacetSortToggled: false
};

export function isFacetsVisible(state = false, action) {
  const { type, visible, pageType } = action;
  switch (type) {
    case TOGGLE_FACETS:
      return visible;
    case PAGE_TYPE_CHANGE:
      return pageType === 'search' ? state : false;
    default:
      return state;
  }
}

export function isFacetableView(state = false, action) {
  const { type, visible } = action;
  switch (type) {
    case TOGGLE_FACETABLE_VIEW:
      return visible;
    default:
      return state;
  }
}

export const sortTopBrands = facet => {
  if (facet.facetField === 'brandNameFacet' && topBrands?.length > 0) {
    const facetValuesCopy = [ ...facet.values ];
    const foundTopValues = facetValuesCopy.filter((v, i) => {
      const foundIndex = topBrands.indexOf(v.name.toLowerCase()) > -1;
      if (foundIndex) {
        facetValuesCopy.splice(i, 1);
        return foundIndex;
      }
    }).sort((a, b) => topBrands.indexOf(a.name) - topBrands.indexOf(b.name));
    facet.values = foundTopValues.concat(facetValuesCopy);
  }
};

export const stripSeoSuppressedUrls = (facets, seoSuppressedUrls = suppressFiltersLinkHrefs) => {
  const mapFacetValues = seoSuppressedUrls ? facet => ({ ...facet, values: facet.values.map(value => {
    const { facetUrl } = value;
    return FILTERS_RE.test(facetUrl) ? { ...value, facetUrl: null } : value;
  }) }) : facet => facet;

  return facets.map(facet => {
    topBrands.length > 0 && sortTopBrands(facet);
    return mapFacetValues(facet);
  });
};

const makePersonalizedSizeInfo = sizes => ` (${sizes[0]}-${sizes[sizes.length - 1]})`;

export default function facets(state = initialState, action) {
  const { type, isFresh, selectedFacetGroupIndex, selectedFacetGroupName, selectedFacetIndex, response, url, isPcm } = action;
  let newState = Immutable(state);
  switch (type) {
    case TOGGLE_SELECTED_FACET_GROUP:
    {
      if (selectedFacetGroupName === 'sort') {
        newState = newState.set('mobileFacetSortToggled', !newState.mobileFacetSortToggled);
        newState = newState.set('chosenFacetGroup', initialState.chosenFacetGroup);
        newState = newState.updateIn([groupName, index, 'isSelected'], () => false);
      }

      const groupName = newState.pcm && newState.pcm.findIndex(f => f.facetField === selectedFacetGroupName) > -1 ? 'pcm' : 'toDisplay';
      const index = (newState[groupName] || []).findIndex(facet => facet.facetField === selectedFacetGroupName);

      if (index > -1) {
        newState = newState.set('mobileFacetSortToggled', false);
        newState = newState.set('chosenFacetGroup', !newState.chosenFacetGroup || (onlyOneGroupOpen && newState.chosenFacetGroup?.facetField !== selectedFacetGroupName) ? newState[groupName][index] : initialState.chosenFacetGroup);
        newState = newState.updateIn([groupName, index, 'isSelected'], v => (v ? false : 'selected'));
        newState = newState.updateIn([groupName, index, 'desktopSelected'], v => (v ? false : 'selected'));
      }
      return newState;
    }
    case TOGGLE_SELECTED_FACET:
    {
      const facetGroupName = isPcm ? 'pcm' : 'toDisplay';
      const facet = newState[facetGroupName][selectedFacetGroupIndex];
      if (facet) {
        if (SINGLE_SELECT_FILTERS[facet.facetField]) {
          // chosen facet is a single-select, so remove the group from the tree
          // since only 1 item should be selected from a single-select at a time.
          newState = newState.update(facetGroupName, items => items.filter((item, i) => i !== selectedFacetGroupIndex));
          newState = newState.set('chosenFacetGroup', initialState.chosenFacetGroup);
        } else {
          newState = newState.updateIn([facetGroupName, selectedFacetGroupIndex, 'values', selectedFacetIndex, 'isSelected'], v => !v);
        }
      }
      return newState;
    }
    case REQUEST_SEARCH:
      if (isFresh) {
        newState = Immutable(initialState);
      }
      return newState.set('requestedUrl', url);
    case RECEIVE_SEARCH_RESPONSE:
      if (response.url === state.requestedUrl) {
        const filters = response.filters || {};

        const sizeKey = response.facets.filter(v => sizeFacetFields.includes(v.facetField));
        if (response.customerPreferences && sizeKey.length) {
          const sizeMessage = `${response.customerPreferences.facets[0].name}${makePersonalizedSizeInfo(response.customerPreferences.sizes)}`;
          response.facets.unshift({ facetField: BEST_FOR_YOU_FACETFIELD, facetFieldDisplayName: 'Your Preferences', values: [{ count: null, name: sizeMessage, confidence: 1 }] });
        }

        newState = newState.update('toDisplay', () => stripSeoSuppressedUrls(response.facets));
        if (newState.toDisplay && newState.toDisplay.length > 0) {
          const newFacets = newState.toDisplay.map(v => {
            v = v.set('values', v.values.map(g => {
              /*
                Display value for facets
                https://github01.zappos.net/mweb/marty/issues/6307
              */
              if (!g.displayName) {
                g = g.set('displayName', g.name);
              }

              if (filters[v.facetField] && filters[v.facetField].indexOf(g.name) > -1) {
                g = g.set('isSelected', true);
              }
              return g;
            }));
            return v;
          });
          newState = newState.set('toDisplay', newFacets);
        }
      }
      return newState;
    case TOGGLE_FACET_CHOSEN:
      newState = newState.set('chosenFacetGroup', initialState.chosenFacetGroup);
      return newState;
    case RESET_FACET_GROUP:
      newState.toDisplay.forEach((v, i) => {
        newState.toDisplay[i].values.forEach((g, ii) => {
          if (newState.toDisplay[i].values[ii].isSelected) {
            newState = newState.setIn(['toDisplay', i, 'values', ii, 'isSelected'], false);
          }
        });
      });
      newState = newState.set('chosenFacetGroup', initialState.chosenFacetGroup);
      return newState;
    default:
      return state;
  }
}
